import ColorPicker from '@/components/commons/color-picker';
import { ElementProps } from '@/components/sections/builder/types';
import BackgroundSetting from '@/components/sections/builder/settings/background-setting';
import ImageSetting from '@/components/sections/builder/settings/image-setting';

import { useBuilderStore } from '@/stores/builder';
import { ColorResult } from 'react-color';
import { shallow } from 'zustand/shallow';
import InputLink from '@/components/sections/builder/settings/input-link';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { TimelineElementDataType } from '@/components/sections/builder/elements/body/timeline/types';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';

const IMAGE_HEIGHT = [
  { value: 'adapt', label: 'Adapt to first Image' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export default function ElementSettingTimeline({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as TimelineElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  return (
    <div>
      <h1 className='text-sm font-bold '>Layout</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Layout</h4>
        <InputSelect
          defaultValue={data.layout}
          options={[
            { label: 'Full Width', value: 'full' },
            { label: 'Grid', value: 'grid' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { layout: v })}
        />
      </div>
      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
      <Separator className='my-4' />
      <h1 className='text-sm font-bold '>Image Settings</h1>
      <div className='mt-2'>Image</div>
      <ImageSetting
        value={data?.image}
        onChange={(v) => builder.setElementData(element.id, { image: v })}
      />
      <div className='mb-2 mt-4'>Mobile image (optional)</div>
      <ImageSetting
        value={data?.mobileImg}
        onChange={(val) => {
          builder.setElementData(element.id, {
            mobileImg: val,
          });
        }}
      />
      <div className='my-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-left text-xs'>Device under this size use mobile image</h4>
        <InputSlider
          value={data.mobileMinWidth || 0}
          max={1440}
          min={0}
          onChange={(val) =>
            builder.setElementData(element.id, {
              totalCol: val,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Image Position</h4>
        <InputSelect
          defaultValue={data.imagePosition}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { imagePosition: v })}
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Image Height</h4>
        <InputSelect
          defaultValue={data.imageHeight}
          options={IMAGE_HEIGHT.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(v) => builder.setElementData(element.id, { imageHeight: v })}
        />
      </div>
      <div className='mb-2 mt-2'>Image Link</div>
      <InputLink
        name='url_path_img'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setElementData(element.id, {
            imageUrl: value,
          });
        }}
        value={data.imageUrl as string}
      />
      <Separator className='my-4' />
      <h1 className='text-sm font-bold '>Style</h1>

      <div className='mt-2 '>Color scheme</div>
      <SchemeSetting
        value={data.colorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mb-4 mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>

      <BackgroundSetting
        value={data.background}
        isShowBackgroundLayout={false}
        onChange={(background) =>
          builder.setElementData(element.id, {
            background,
          })
        }
      />
      {data?.background?.type !== 'image' && (
        <>
          <div className='mb-2 mt-4 '>Overlay</div>
          <ColorPicker
            color={data.overlay}
            inputColor={data.overlay}
            onChangeColorComplete={(color) => onChangeColor(color)}
            onChangeInputColor={(e) =>
              builder.setElementData(element.id, { overlay: e.target.value })
            }
            onChangePercentage={(val) =>
              builder.setElementData(element.id, { overlayPercentage: val })
            }
          />
        </>
      )}
    </div>
  );
}
